import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Cable, MessageCircle, Sparkles, Mail, Megaphone, Mic, FolderOpen, KeyRound, BookOpen, Volume2, Repeat,
  Download, AlertTriangle, ArrowRight, Settings2, type LucideIcon,
} from 'lucide-react';
import * as integracionesApi from '@/api/integraciones.api';
import type { EstadoIntegracion, Integracion } from '@/api/integraciones.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/hooks/useToast';
import { useMenu } from '@/hooks/useMenu';

/**
 * HUB DE INTEGRACIONES (estándar Codeus): lo que está conectado al negocio, cómo se
 * está portando, por dónde entra cada dato, a qué campo va y sus registros en CSV.
 * Todo lo dice el servidor; esta pantalla solo lo pinta.
 */
const ICONO: Record<string, LucideIcon> = {
  whatsapp: MessageCircle, ia: Sparkles, correo: Mail, anuncios: Megaphone, transcripcion: Mic,
  archivos: FolderOpen, llaves: KeyRound, notion: BookOpen, voz: Volume2, relevo: Repeat,
};
const ESTADO: Record<EstadoIntegracion, { texto: string; variante: 'success' | 'danger' | 'default' }> = {
  activa: { texto: 'Funcionando', variante: 'success' },
  con_problemas: { texto: 'Con problemas', variante: 'danger' },
  sin_configurar: { texto: 'Sin configurar', variante: 'default' },
};
const ISO = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g;
/** Las fechas llegan en ISO; se leen en hora de aquí. */
const legible = (s: string) => s.replace(ISO, (iso) => new Date(iso).toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' }));

function Detalle({ i, onClose }: { i: Integracion | null; onClose: () => void }) {
  const toast = useToast();
  // «Configurar en …» solo si esa pantalla está en el menú de quien mira: si no, sería un enlace que rebota.
  const { datos: menu } = useMenu();
  const puedeConfigurar = (ruta: string) => Boolean(menu?.rutas.some((r) => r.ruta === ruta));
  const [dias, setDias] = useState<1 | 7 | 30>(7);
  const bajar = useMutation({
    mutationFn: () => integracionesApi.descargarRegistros(i!.id, dias),
    onSuccess: () => toast.success('Registros descargados'),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'No se pudieron descargar los registros'),
  });
  if (!i) return null;
  const Icono = ICONO[i.id] ?? Cable;
  const titulo = (t: string) => <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t}</h3>;

  return (
    <Modal open onClose={onClose} size="xl" title={i.nombre} description={i.paraQue}
      footer={<Button variant="ghost" onClick={onClose}>Cerrar</Button>}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"><Icono className="h-4 w-4" /></span>
          <Badge variant={ESTADO[i.estado].variante}>{ESTADO[i.estado].texto}</Badge>
          <span className="text-sm text-slate-600 dark:text-slate-300">{i.resumen}</span>
          {i.configurarEn && puedeConfigurar(i.configurarEn.ruta) && (
            <Link to={i.configurarEn.ruta} className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline dark:text-brand-300">
              <Settings2 className="h-4 w-4" /> Configurar en {i.configurarEn.etiqueta}
            </Link>
          )}
        </div>

        {i.monitor.length > 0 && (
          <section className="space-y-2">
            {titulo('Cómo se está portando')}
            <dl className="grid gap-2 sm:grid-cols-2">
              {i.monitor.map((m) => (
                <div key={m.nombre} className={`rounded-lg border px-3 py-2 ${m.alerta ? 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-900/20' : 'border-surface-border dark:border-slate-800'}`}>
                  <dt className="text-xs text-slate-500 dark:text-slate-400">{m.nombre}</dt>
                  <dd className={`text-sm font-semibold tabular-nums ${m.alerta ? 'text-danger' : 'text-slate-900 dark:text-slate-100'}`}>{legible(m.valor)}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {i.avisos.length > 0 && (
          <ul className="space-y-1.5">
            {i.avisos.map((a) => (
              <li key={a} className="flex gap-2 text-sm text-amber-700 dark:text-amber-300"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{a}</li>
            ))}
          </ul>
        )}

        <section className="space-y-2">
          {titulo('Configuración')}
          <dl className="divide-y divide-surface-border rounded-lg border border-surface-border dark:divide-slate-800 dark:border-slate-800">
            {i.configuracion.map((c) => (
              <div key={c.nombre} className="grid gap-1 px-3 py-2 sm:grid-cols-[minmax(0,14rem)_1fr] sm:gap-4">
                <dt className="text-sm text-slate-500 dark:text-slate-400">{c.nombre}</dt>
                <dd className="min-w-0 break-words text-sm text-slate-900 dark:text-slate-100">{c.valor}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="space-y-2">
          {titulo('Por dónde entra cada dato')}
          <ul className="space-y-2">
            {i.rutas.map((r) => (
              <li key={r.dato} className="rounded-lg border border-surface-border px-3 py-2 text-sm dark:border-slate-800">
                <p className="font-medium text-slate-900 dark:text-slate-100">{r.dato}</p>
                <p className="flex flex-wrap items-center gap-1.5 text-slate-600 dark:text-slate-300">
                  <span>{r.entraPor}</span><ArrowRight className="h-3.5 w-3.5 shrink-0 text-slate-400" /><span>{r.vaA}</span>
                </p>
              </li>
            ))}
          </ul>
        </section>

        {i.campos.length > 0 && (
          <section className="space-y-2">
            {titulo('A qué campo va cada dato')}
            <div className="overflow-x-auto rounded-lg border border-surface-border dark:border-slate-800">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                  <tr><th className="px-3 py-2 text-left font-medium">Llega como</th><th className="px-3 py-2 text-left font-medium">Se guarda en</th></tr>
                </thead>
                <tbody className="divide-y divide-surface-border dark:divide-slate-800">
                  {i.campos.map((c) => (
                    <tr key={c.origen}><td className="px-3 py-2 text-slate-700 dark:text-slate-300">{c.origen}</td><td className="px-3 py-2 text-slate-900 dark:text-slate-100">{c.destino}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <section className="space-y-2">
          {titulo('Registros')}
          {i.registros ? (
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-40">
                <Select label="Periodo" value={String(dias)} onChange={(e) => setDias(Number(e.target.value) as 1 | 7 | 30)}
                  options={[{ value: '1', label: 'Último día' }, { value: '7', label: 'Últimos 7 días' }, { value: '30', label: 'Últimos 30 días' }]} />
              </div>
              <Button variant="outline" onClick={() => bajar.mutate()} loading={bajar.isPending}><Download className="h-4 w-4" /> Descargar CSV</Button>
              <p className="w-full text-xs text-slate-400">Se abre en Excel. Queda anotado quién lo descargó.</p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">Esta integración todavía no deja registros para descargar.</p>
          )}
        </section>
      </div>
    </Modal>
  );
}

export default function Integraciones() {
  const { data, isLoading, error } = useQuery({ queryKey: ['integraciones'], queryFn: integracionesApi.getIntegraciones, refetchInterval: 60_000 });
  const [abierta, setAbierta] = useState<string | null>(null);

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;
  if (error || !data) {
    return (
      <div className="p-6">
        <EmptyState icon={Cable} title="No tienes acceso a las integraciones" description={error instanceof ApiError ? error.message : 'Pídeselo a quien maneja los roles de tu negocio.'} />
      </div>
    );
  }

  const cuantas = (e: EstadoIntegracion) => data.filter((i) => i.estado === e).length;
  const conProblemas = cuantas('con_problemas');
  const sinConfigurar = cuantas('sin_configurar');
  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Integraciones"
        description="Lo que está conectado a tu negocio, cómo se está portando y por dónde entra cada dato."
      />
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {cuantas('activa')} funcionando
        {conProblemas > 0 && <> · <span className="font-medium text-danger">{conProblemas} con problemas</span></>}
        {sinConfigurar > 0 && <> · {sinConfigurar} sin configurar</>}
        <span className="text-slate-400"> · se actualiza cada minuto</span>
      </p>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {data.map((i) => {
          const Icono = ICONO[i.id] ?? Cable;
          return (
            <Card key={i.id} className={i.estado === 'con_problemas' ? 'border-red-300 dark:border-red-900' : undefined}>
              <CardBody className="flex h-full flex-col gap-3">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"><Icono className="h-4 w-4" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{i.nombre}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{i.resumen}</p>
                  </div>
                  <Badge variant={ESTADO[i.estado].variante}>{ESTADO[i.estado].texto}</Badge>
                </div>
                <p className="flex-1 text-sm text-slate-600 dark:text-slate-300">{i.paraQue}</p>
                <Button variant="outline" size="sm" className="self-start" onClick={() => setAbierta(i.id)}>Ver detalle</Button>
              </CardBody>
            </Card>
          );
        })}
      </div>
      <Detalle i={data.find((x) => x.id === abierta) ?? null} onClose={() => setAbierta(null)} />
    </div>
  );
}
